import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { canWriteModule } from "@/lib/access";
import { Plus, Pencil, Archive, Loader2, HeartHandshake, Search, Download, FileText, FileSpreadsheet, ChevronDown, ShieldCheck } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { viewFromApi } from "@/components/FileViewer";
import { toast } from "sonner";
import { useDebounced } from "@/lib/useDebounced";
import { LoadMoreBar, PAGE_SIZE as SERVER_PAGE, totalFrom } from "@/components/LoadMoreBar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ClientGrid } from "@/components/care/ClientGrid";

import { ClientEditorDialog, emptyClient as empty } from "@/components/care/ClientEditorDialog";

const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/15";
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => today().slice(0, 8) + "01";
const downloadCsv = async (includeArchived) => {
  const res = await api.get("/clients/export.csv", { params: { include_archived: includeArchived }, responseType: "blob" });
  const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "clients.csv"; a.click(); URL.revokeObjectURL(url);
};

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const dq = useDebounced(q.trim(), 300);
  const [total, setTotal] = useState(0);
  const [moreLoading, setMoreLoading] = useState(false);
  const [archived, setArchived] = useState(false);
  const [editor, setEditor] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = canWriteModule(user, "clients");
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");
  useEffect(() => {
    if (!editParam) return;
    api.get("/clients", { params: { include_archived: true } }).then(({ data }) => {
      const c = data.find((x) => x.id === editParam);
      if (c) setEditor({ ...empty, ...c }); else toast.error("Client not found");
    });
    setSearchParams((sp) => { sp.delete("edit"); return sp; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editParam]);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/clients", { params: { include_archived: archived, q: dq || undefined, limit: SERVER_PAGE } });
      setClients(res.data); setTotal(totalFrom(res));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load clients"); }
    finally { setLoading(false); }
  }, [archived, dq]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const archive = async (c) => {
    if (!window.confirm(`${c.active ? "Archive" : "Restore"} ${c.full_name}?`)) return;
    await api.delete(`/clients/${c.id}`, { params: { restore: !c.active } });
    load();
  };

  const shown = clients; // search is server-side
  const onSite = clients.filter((c) => c.on_site).length;
  const expected = clients.filter((c) => c.expected_today && !c.on_site).length;

  return (
    <div className="p-6 md:p-10" data-testid="clients-page">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Personal care home</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><HeartHandshake className="h-7 w-7 text-primary" /> Clients</h1>
          <p className="text-sm text-slate-500">
            <b className="text-emerald-700" data-testid="clients-onsite-count">{onSite} on site</b> · <span className="text-amber-700">{expected} expected, not arrived</span> · {clients.length} total
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="clients-export-menu" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 transition">
                <Download className="h-4 w-4" /> Export <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-widest text-slate-500">Client report</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => viewFromApi(api, "/clients/report.pdf", { params: { include_archived: archived }, title: "Client roster report", filename: "client-roster.pdf", type: "application/pdf" })} data-testid="clients-export-pdf" className="gap-2 cursor-pointer"><FileText className="h-4 w-4 text-slate-500" /> Client roster (PDF)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadCsv(archived)} data-testid="clients-export-csv" className="gap-2 cursor-pointer"><FileSpreadsheet className="h-4 w-4 text-slate-500" /> Client list (CSV)</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[11px] uppercase tracking-widest text-slate-500">Attendance report</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => viewFromApi(api, "/stays/attendance-report.pdf", { params: { from: monthStart(), to: today() }, title: "Attendance report", filename: "attendance-report.pdf", type: "application/pdf" })} data-testid="clients-attendance-pdf" className="gap-2 cursor-pointer"><FileText className="h-4 w-4 text-slate-500" /> This month — all clients (PDF)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {canWrite && <button onClick={() => setEditor({ ...empty })} data-testid="add-client-btn" className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:shadow transition">
            <Plus className="h-4 w-4" /> Add client
          </button>}
        </div>
      </header>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…" data-testid="clients-search" className={`${inputCls} pl-9`} />
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} data-testid="clients-show-archived" /> Show archived</label>
      </div>
      {loading ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div> : (
        <ClientGrid clients={shown} onChanged={load} onOpen={(c) => navigate(`/admin/clients/${c.id}`)} extraActions={(c) => (
          <>
            <button onClick={() => viewFromApi(api, `/clients/${c.id}/record.pdf`, { title: `${c.full_name} — full record`, filename: `record-${c.id}.pdf`, type: "application/pdf" })} data-testid={`client-record-${c.id}`} title="Download full record (HIPAA right of access)" className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 inline-flex items-center justify-center"><ShieldCheck className="h-3.5 w-3.5" /></button>
            {canWrite && c.active && <button onClick={() => setEditor({ ...empty, ...c })} data-testid={`client-edit-${c.id}`} title="Edit" className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 inline-flex items-center justify-center"><Pencil className="h-3.5 w-3.5" /></button>}
            {canWrite && <button onClick={() => archive(c)} data-testid={`client-archive-${c.id}`} title={c.active ? "Archive" : "Restore"} className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 inline-flex items-center justify-center"><Archive className="h-3.5 w-3.5" /></button>}
          </>
        )} />
      )}
      {!loading && total > clients.length && (
        <LoadMoreBar shown={clients.length} total={total} loading={moreLoading} testid="clients-load-more" onMore={async () => {
          setMoreLoading(true);
          try { const res = await api.get("/clients", { params: { include_archived: archived, q: dq || undefined, limit: SERVER_PAGE, offset: clients.length } }); setClients((p) => [...p, ...res.data]); setTotal(totalFrom(res)); }
          finally { setMoreLoading(false); }
        }} />
      )}

      {editor && <ClientEditorDialog initial={editor} onClose={() => setEditor(null)} onSaved={load} />}
    </div>
  );
}
